import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { syncPush } from '../lib/push';
import { Menu, X, ShoppingBag, User, LogOut } from 'lucide-react';

const buildLinks = (user) => [
  { to: '/', label: 'Inicio', id: 'home' },
  { to: user ? '/mis-asesorias' : '/asesorias', label: 'Asesorías', id: 'asesorias' },
  { to: '/conversaciones', label: 'Conversaciones', id: 'conversaciones' },
  { to: 'https://www.stilodecor.com', label: 'Stilo Decor', id: 'stilo-decor', external: true },
];

export default function Header() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const links = buildLinks(user);
  const loc = useLocation();
  const [unread, setUnread] = useState(0);

  useEffect(() => { syncPush(); }, [user]);

  useEffect(() => {
    if (!user) { setUnread(0); return undefined; }
    const fetchUnread = () => api.get('/unread').then(r => setUnread(r.data.total)).catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    window.addEventListener('stilo:unread-refresh', fetchUnread);
    return () => { clearInterval(t); window.removeEventListener('stilo:unread-refresh', fetchUnread); };
  }, [user, loc.pathname]);

  const dot = (l, p = "") => l.id === 'conversaciones' && unread > 0 && (
    <span className="ml-1.5 inline-block w-2 h-2 rounded-full bg-stilo-gold align-middle" title={`${unread} sin leer`} data-testid={`${p}unread-dot-${l.id}`} />
  );

  return (
    <header className="sticky top-0 z-50 bg-stilo-bg/85 backdrop-blur-md" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="brand-logo">
          <img src="/brand/studio-stilo-monogram.png" alt="Studio Stilo monograma" className="h-10 w-10 object-contain transition-transform duration-500 group-hover:-rotate-6" />
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-serif text-[22px] tracking-tight text-stilo-ink">Studio Stilo</span>
            <span className="overline mt-1 text-[10px]">Diseño de Interiores</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map(l => (l.external ? (
            <a key={l.id} href={l.to} target="_blank" rel="noreferrer" data-testid={`nav-${l.id}`}
              className="font-display text-[11px] font-medium tracking-[0.22em] uppercase transition-colors text-stilo-ink2 hover:text-stilo-ink">{l.label}</a>
          ) : (
            <NavLink
              key={l.id}
              to={l.to}
              end={l.to === '/'}
              className={({ isActive }) =>
                `font-display text-[11px] font-medium tracking-[0.22em] uppercase transition-colors ${isActive ? 'text-stilo-ink' : 'text-stilo-ink2 hover:text-stilo-ink'}`}
              data-testid={`nav-${l.id}`}
            >
              {l.label}{dot(l)}
            </NavLink>
          )))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          {user ? (
            <>
              <Link to={user.role === 'designer' ? '/designer' : '/cuenta'} className="flex items-center gap-2 text-sm text-stilo-ink2 hover:text-stilo-ink" data-testid="nav-account">
                <User className="w-4 h-4" strokeWidth={1.5} />
                <span>{user.name.split(' ')[0]}</span>
              </Link>
              <button onClick={() => { logout(); nav('/'); }} className="text-stilo-ink2 hover:text-stilo-terra" data-testid="logout-btn">
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm text-stilo-ink2 hover:text-stilo-ink" data-testid="nav-login">Acceder</Link>
              <Link to="/registro" className="btn-stilo" data-testid="nav-register">Reservar</Link>
            </>
          )}
        </div>

        <button onClick={() => setOpen(!open)} className="lg:hidden relative text-stilo-ink" data-testid="mobile-menu-toggle">{unread > 0 && !open && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-stilo-gold" data-testid="unread-dot-mobile" />}
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-stilo-ink/10 bg-stilo-bg" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map(l => (
              l.external ? (
                <a key={l.id} href={l.to} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.id}`} className="font-display text-xs tracking-[0.22em] uppercase text-stilo-ink py-1">{l.label}</a>
              ) : (
                <Link key={l.id} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.id}`} className="font-display text-xs tracking-[0.22em] uppercase text-stilo-ink py-1">{l.label}{dot(l, "mobile-")}</Link>
              )
            ))}
            {user ? (
              <>
                <Link to={user.role === 'designer' ? '/designer' : '/cuenta'} onClick={() => setOpen(false)} className="text-stilo-ink py-1">Mi cuenta</Link>
                <button onClick={() => { logout(); setOpen(false); nav('/'); }} className="text-left text-stilo-terra py-1">Cerrar sesión</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="text-stilo-ink py-1">Acceder</Link>
                <Link to="/registro" onClick={() => setOpen(false)} className="btn-stilo w-fit">Reservar</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
